import { fixImageUrl } from '../../../api'

/**
 * Etape 1 (parcours A) : « Quel produit ? »
 *
 * REMISE A LA FORME DU PROTOTYPE (S67, remonte par Enzo)
 * -----------------------------------------------------
 * La S66 avait corrige la COQUE du wizard (feuille montante) mais pas son
 * CONTENU — et c'est le contenu qu'Enzo regardait. L'ecart etait structurel,
 * pas decoratif :
 *
 *  - l'ecran imposait une navigation en PROFONDEUR : 4 grosses cartes de zone
 *    en grille 2x2, puis les categories de la zone, puis les produits. Trois
 *    niveaux pour atteindre une tomate. Le prototype est PLAT : une recherche,
 *    des pastilles de rayon, la liste. On voit les produits tout de suite ;
 *  - chaque produit portait un gros bouton `+` en degrade, qui se lit comme une
 *    action distincte du reste de la carte. Le prototype met un simple chevron
 *    `›` : toute la ligne est cliquable, il n'y a rien a viser ;
 *  - « Mon produit n'est pas dans la liste » etait une carte a deux lignes avec
 *    une icone ✨. Le prototype en fait un bouton **pointille orange** d'une
 *    ligne, en PIED de resultats, dont le libelle change quand la recherche ne
 *    donne rien — jamais d'ecran vide, jamais de cul-de-sac.
 *
 * Nos donnees sont plus riches que celles du prototype (qui a un catalogue
 * alimentaire en dur) : les pastilles listent donc nos CATEGORIES reelles — et
 * seulement celles qui contiennent des produits, sinon on offre des filtres qui
 * ne filtrent rien. Le niveau « zone » du modele de donnees n'a pas d'equivalent
 * dans le prototype : il disparait de cet ecran (il reste utilise par le
 * parcours « proposer un produit », ou il aide a classer).
 *
 * Regle ecrite : `docs/DESIGN-GUIDE.md` §11.5.
 */
function StepBrowse({
  tc, ts, cardBg,
  search, onSearchChange,
  categories,
  categoryId, onCategorySelect,
  catalog, catLoading,
  onSelectProduct,
  onSwitchToPropose,
}) {
  // Un filtre qui ne filtre rien n'a pas sa place : on ne garde que les
  // categories peuplees (`_count.products`, servi par l'API).
  const rayons = (categories || []).filter((c) => (c._count?.products ?? 1) > 0)

  return (
    <div>
      {/* Intro + acces direct a la proposition (prototype) */}
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <p className={`text-[12px] ${ts}`}>Cherchez dans le référentiel</p>
        <button
          onClick={onSwitchToPropose}
          className="panneau-verre rounded-full px-3.5 py-[7px] text-[12px] font-bold flex-shrink-0"
          style={{ border: '1px solid rgb(var(--c-primary) / 0.35)', color: 'rgb(var(--c-primary))' }}
        >
          + Proposer
        </button>
      </div>

      <input
        type="text"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Ex. mangue, dachine, patate…"
        className="w-full rounded-2xl px-[15px] py-[13px] text-base text-ink outline-none mb-3"
        style={{ background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(7,62,61,0.12)' }}
      />

      {/* Rayons a plat — « Tous les produits » puis les categories peuplees */}
      {rayons.length > 0 && (
        <div className="flex flex-wrap gap-[7px] mb-3">
          {[null, ...rayons].map((cat) => {
            const actif = (cat?.id ?? null) === (categoryId ?? null)
            return (
              <button
                key={cat?.id || 'tous'}
                onClick={() => onCategorySelect(cat?.id ?? null)}
                className="rounded-full px-[13px] py-[7px] text-[12px] font-bold transition-colors"
                style={
                  actif
                    ? { background: 'rgb(var(--c-green))', color: 'rgb(var(--c-creme))' }
                    : { background: 'rgba(255,255,255,0.6)', color: 'rgba(7,62,61,0.6)' }
                }
              >
                {cat ? cat.nom : 'Tous les produits'}
              </button>
            )
          })}
        </div>
      )}

      <ListeProduits
        tc={tc} ts={ts} cardBg={cardBg}
        loading={catLoading}
        produits={catalog}
        recherche={search}
        onSelectProduct={onSelectProduct}
        onSwitchToPropose={onSwitchToPropose}
      />
    </div>
  )
}

function ListeProduits({ tc, ts, cardBg, loading, produits, recherche, onSelectProduct, onSwitchToPropose }) {
  if (loading) {
    return (
      <div className="flex flex-col gap-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-[62px] rounded-2xl animate-pulse bg-ink/5" />
        ))}
      </div>
    )
  }

  const vide = !produits || produits.length === 0
  const q = (recherche || '').trim()

  return (
    <div className="flex flex-col gap-2">
      {(produits || []).map((p, i) => (
        <button
          key={p.id}
          onClick={() => onSelectProduct(p)}
          className={`w-full text-left rounded-2xl px-3.5 py-3 border flex items-center gap-3 active:scale-[0.99] transition-transform animate-fade-up ${cardBg}`}
          style={{ animationDelay: `${Math.min(i, 12) * 0.02}s` }}
        >
          {p.photos?.length > 0 ? (
            <img
              src={fixImageUrl(p.photos[0])}
              alt=""
              className="w-7 h-7 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <span className="text-2xl leading-none flex-shrink-0">{p.category?.emoji || '📦'}</span>
          )}
          <span className="flex-1 min-w-0">
            <span className={`block text-sm font-bold truncate ${tc}`}>{p.nom}</span>
            <span className={`block text-[11px] ${ts}`}>
              {[p.category?.nom, p.unit || 'kg'].filter(Boolean).join(' · ')}
            </span>
          </span>
          <span className="text-sm flex-shrink-0 text-ink/40">›</span>
        </button>
      ))}

      {/* Jamais d'ecran vide : la proposition vit en PIED de resultats, et son
          libelle dit ce qui vient de se passer (prototype). */}
      <button
        onClick={onSwitchToPropose}
        className="rounded-2xl px-3.5 py-[13px] text-[13px] font-bold text-center"
        style={{
          border: '1.5px dashed rgb(var(--c-primary) / 0.4)',
          background: 'rgb(var(--c-primary) / 0.06)',
          color: 'rgb(var(--c-primary))',
        }}
      >
        {vide && q
          ? `Aucun résultat pour « ${q} » — le proposer`
          : 'Pas dans la liste ? Proposer un produit'}
      </button>
    </div>
  )
}

export default StepBrowse
